import {
  type ReactNode,
  useMemo,
  useRef,
  Children,
  type ReactElement,
} from 'react';
import { useIntl } from 'react-intl';

import { ChevronDownSmallIcon } from '@dynatrace/strato-icons';

import { Button } from '../../buttons/button/Button.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useId } from '../../core/hooks/useId.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { Select } from '../../forms/select/Select.js';
import { SelectMultiValue } from '../../forms/select/types.js';
import { FormControlWithOverlayRef } from '../../forms/shared-types.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useFilterBarContext } from './hooks/useFilterBarContext.js';
import { Item } from './Item.js';
import { FilterBarActions } from './types/filter-state.types.js';
import { getTextFromNode } from './utils/getTextFromNode.js';

type MoreMenuProps = {
  children: ReactNode[];
};

/**
 * The moreMenu allows to hide and show further filters. All filterBar.Items inside are hidden initially.
 * @internal
 */
export const MoreMenu = ({ children }: MoreMenuProps) => {
  const intl = useIntl();
  const label = intl.formatMessage({
    defaultMessage: 'Add filter',
    id: '+BUznwQEJTiILiAa',
    description:
      'Message to display by default for adding more Filters to the FilterBar.',
  });

  const { focusClassName, focusProps } = useFocusRing({
    color: 'neutral',
    focusWithin: true,
    ignoreModality: true,
  });

  const { dispatch, setLastAddedItem, state, filterBarRootId } =
      useFilterBarContext(),
    { itemsPinnedState } = state;

  const selectRef = useRef<FormControlWithOverlayRef | null>(null);

  const allPinnableFilters = useMemo<
    Record<string, { elem: ReactElement; label: string }>
  >(() => {
    const temp: Record<string, { elem: ReactElement; label: string }> = {};
    for (const child of Children.toArray(children)) {
      if (isSlot(child, Item)) {
        const uniqueName: string = child.props.name;
        temp[uniqueName] = {
          elem: child,
          label: getTextFromNode(child.props.label, intl, uniqueName),
        };
      }
    }
    return temp;
  }, [children, intl]);

  const optionalPinnedItems = useMemo(
    () =>
      Object.entries(itemsPinnedState || {}).reduce(
        (keys, [key, value]) => {
          if (value === 'pinned-optional') {
            keys.push(key);
          }
          return keys;
        },
        [] as Array<string | number>,
      ),
    [itemsPinnedState],
  );

  const handleSelectChange = (newValues: SelectMultiValue<string | number>) => {
    setLastAddedItem(null);

    const union = new Set([
      ...(newValues ?? []),
      ...(optionalPinnedItems || []),
    ]);

    // Check for newly selected items and pin those
    if (
      !optionalPinnedItems ||
      (newValues && optionalPinnedItems.length < newValues.length)
    ) {
      const filtersToPin = [...union].filter(
        (key) => !optionalPinnedItems?.includes(key),
      );

      selectRef?.current?.close();
      filtersToPin?.length && setLastAddedItem(filtersToPin[0]?.toString());

      dispatch({
        type: FilterBarActions.PIN,
        payload: {
          keys: filtersToPin,
        },
      });

      return;
    }

    // Check for newly unselected items and unpin those
    if (!newValues) {
      dispatch({
        type: FilterBarActions.UNPIN,
        payload: {
          keys: optionalPinnedItems ?? [],
        },
      });
    } else if (
      optionalPinnedItems &&
      optionalPinnedItems.length > newValues.length
    ) {
      const keys = [...union].filter((key) => !newValues?.includes(key));

      dispatch({
        type: FilterBarActions.UNPIN,
        payload: { keys },
      });
    }
  };

  const labelId = useId();

  return (
    <>
      {optionalPinnedItems.map(
        (key) =>
          allPinnableFilters[key] && (
            <Flex key={key} gap={2} alignItems="flex-end">
              {allPinnableFilters[key].elem}
            </Flex>
          ),
      )}
      <div style={{ maxWidth: '100%' }} {...focusProps}>
        <Select
          value={optionalPinnedItems}
          onChange={handleSelectChange}
          id="user-select"
          multiple={true}
          clearable
          ref={selectRef}
        >
          <Select.CustomTrigger>
            <Button
              as="div"
              id={labelId}
              className={focusClassName}
              data-dt-children-name="AddFilter"
            >
              {label}
              <Button.Suffix>
                <ChevronDownSmallIcon />
              </Button.Suffix>
            </Button>
          </Select.CustomTrigger>
          <Select.Content
            width="fit-content"
            data-dt-ancestor={`#${filterBarRootId}`}
          >
            <Select.Filter />
            {Object.keys(allPinnableFilters).map((key) => (
              <Select.Option value={key} key={key}>
                {allPinnableFilters[key].label}
              </Select.Option>
            ))}
          </Select.Content>
        </Select>
      </div>
    </>
  );
};

(MoreMenu as typeof MoreMenu & { displayName: string }).displayName =
  'FilterBar.MoreMenu';
